import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/compat/router';

import { FullPageLoader } from 'shared/components';
import { getComponentDisplayName } from 'src/utils/get-component-display-name';
import useUser from 'src/hooks/use-user';
import useUI from 'src/hooks/use-ui';
import ROUTES from 'src/routes';

const { EMBEDDED_MENU, STORES, MOBILE_ECOMM, HOME } = ROUTES;

type UseRouteToHomeReturn = {
  isReady: boolean;
  handleRouteToHome: () => void;
};

function getRouteToHome(isEmbedded: string, isStoreFront: string, isMobileEcommApp: string, cName: string): string {
  if (isEmbedded) {
    return `${EMBEDDED_MENU}/${cName}`;
  }
  if (isStoreFront) {
    return `${STORES}/${cName}`;
  }
  if (isMobileEcommApp) {
    return `${MOBILE_ECOMM}/${cName}`;
  }
  return HOME;
}

function useRouteToHome(): UseRouteToHomeReturn {
  const { isEmbedded, isStoreFront, isMobileEcommApp } = useUI();
  const { query: { cName } = { cName: undefined }, replace, isReady } = useRouter() ?? {
    query: {},
    replace: undefined,
    isReady: false,
  };

  const handleRouteToHome = useCallback(() => {
    if (!isReady) {
      return;
    }

    if (typeof cName !== 'string') {
      throw new Error(`ERROR: missing dispensary cName`);
    }

    void replace(getRouteToHome(isEmbedded, isStoreFront, isMobileEcommApp, cName));
  }, [isReady, cName, replace, isEmbedded, isStoreFront, isMobileEcommApp]);

  return {
    isReady,
    handleRouteToHome,
  };
}

export function withLoginRequired<TProps extends Record<string, unknown>>(
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Component: (props: TProps) => JSX.Element | null
): (props: TProps) => JSX.Element | null {
  const WrappedComponent = observer((props: TProps) => {
    const { isLoggedIn, token } = useUser();
    const { isReady, handleRouteToHome } = useRouteToHome();

    useEffect(() => {
      if (isLoggedIn || token) {
        return;
      }
      if (!isReady) {
        return;
      }

      handleRouteToHome();
    }, [isLoggedIn, isReady, token, handleRouteToHome]);

    return isLoggedIn ? <Component {...props} /> : <FullPageLoader />;
  });

  WrappedComponent.displayName = `WithLoginRequired(${getComponentDisplayName(Component)})`;

  return WrappedComponent;
}
